import { CheckIcon as Check, ChevronDownIcon as ChevronDown, XMarkIcon as X } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onCreateNew?: () => void;
  createNewLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  onCreateNew,
  createNewLabel = 'Create New',
  disabled = false,
  loading = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

  // Get selected option
  const selectedOption = options.find(opt => opt.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputClick = () => {
    if (!disabled && !loading) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Search Input (replaces trigger button) */}
      <div className='relative'>
        <input
          ref={inputRef}
          type='text'
          value={isOpen ? searchTerm : selectedOption?.label || ''}
          onChange={handleInputChange}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={`
            w-full h-[46px] px-5 pr-20 border-2 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-primary/10 focus:border-primary
            transition-colors
            ${disabled || loading ? 'bg-slate-50 cursor-not-allowed' : 'bg-white cursor-text hover:border-primary/40'}
            ${isOpen ? 'border-primary ring-2 ring-primary/10' : 'border-slate-100'}
          `}
        />
        <div className='absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1'>
          {selectedOption && !disabled && !isOpen && (
            <button type='button' onClick={handleClear} className='p-1 hover:bg-gray-100 rounded'>
              <X className='h-4 w-4 text-gray-400 hover:text-gray-600' />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className='absolute z-50 w-full mt-1 bg-white border-2 border-slate-100 rounded-lg shadow-lg max-h-80 overflow-hidden'>
          {/* Options List */}
          <div className='max-h-60 overflow-y-auto'>
            {loading ? (
              <div className='px-4 py-8 text-center text-gray-500'>Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => handleSelect(option.value)}
                  className={`
                    w-full text-left px-4 py-2.5 hover:bg-secondary transition-colors
                    flex items-center justify-between gap-2
                    ${value === option.value ? 'bg-secondary border-l-4 border-primary' : 'border-l-4 border-transparent'}
                  `}
                >
                  <span className='font-medium text-gray-900'>{option.label}</span>
                  {value === option.value && <Check className='h-4 w-4 text-primary flex-shrink-0' />}
                </button>
              ))
            ) : (
              <div className='px-4 py-8 text-center text-gray-500'>
                No options found
                {searchTerm && ` for "${searchTerm}"`}
              </div>
            )}
          </div>

          {/* Create New Button */}
          {onCreateNew && (
            <div className='border-t border-gray-200 p-2'>
              <button
                type='button'
                onClick={() => {
                  onCreateNew();
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className='w-full px-3 py-2 text-primary hover:bg-secondary rounded-md font-medium transition-colors text-sm'
              >
                + {createNewLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
